// src/routes/Commish.tsx
import { useMutation, useQuery } from 'convex/react';
import { type FormEvent, useState } from 'react';
import { api } from '../../convex/_generated/api';

export default function Commish() {
  // listFranchises is readable by any signed-in user; we use it to detect
  // the un-seeded bootstrap case where amICommissioner is necessarily false
  // (no franchise exists yet to bind isCommissioner=true to).
  const franchises = useQuery(api.franchises.listFranchises);
  const isCommissioner = useQuery(api.franchises.amICommissioner);
  const seed = useMutation(api.seed.seedLeague);
  const [seasonYear, setSeasonYear] = useState(new Date().getFullYear());
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  if (franchises === undefined || isCommissioner === undefined) {
    return <div className="p-6">Loading…</div>;
  }

  const seeded = franchises.length > 0;

  // Post-seed: gate on commissioner role.
  if (seeded && !isCommissioner) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">Commissioner only</h1>
        <p className="mt-2 text-sm text-gray-600">You're not the commissioner.</p>
      </div>
    );
  }

  async function handleSeed() {
    setError(null);
    setSeeding(true);
    try {
      await seed({ seasonYear });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold">Commissioner</h1>
      <section className="mt-6">
        <h2 className="text-lg font-semibold">Seed league</h2>
        {seeded ? (
          <p className="mt-2 text-sm text-gray-600">
            Already seeded with {franchises.length} franchises. Re-seeding requires deleting tables
            in the Convex dashboard.
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-gray-600">
              No franchises yet. Click below to create the 10 league franchises and the current
              season. Only the configured commissioner email can run this — the server checks{' '}
              <code className="rounded bg-gray-100 px-1">COMMISSIONER_EMAIL</code>.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <label className="text-sm">
                Season year{' '}
                <input
                  type="number"
                  value={seasonYear}
                  onChange={(e) => setSeasonYear(Number(e.target.value))}
                  className="ml-1 w-24 rounded border px-2 py-1"
                />
              </label>
              <button
                type="button"
                onClick={handleSeed}
                disabled={seeding}
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
              >
                {seeding ? 'Seeding…' : 'Seed league'}
              </button>
            </div>
          </>
        )}
        {error && (
          <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </section>
      {seeded && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold">Franchise claim status</h2>
          <table className="mt-2 w-full border-collapse">
            <thead>
              <tr className="border-b text-left text-sm text-gray-500">
                <th className="py-2">Name</th>
                <th>Division</th>
                <th>Clerk subject</th>
                <th>Commissioner</th>
              </tr>
            </thead>
            <tbody>
              {franchises.map((f) => (
                <tr key={f._id} className="border-b">
                  <td className="py-2">{f.name}</td>
                  <td>{f.division}</td>
                  <td className="font-mono text-xs">
                    {f.clerkUserId ?? <span className="text-gray-400">—</span>}
                  </td>
                  <td>{f.isCommissioner ? '✓' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {seeded && isCommissioner && <SeasonRfaSection />}
    </div>
  );
}

/** RFA Phase 4 §4.4 — season rollover + auction lifecycle controls.
 *  Rendered only for the commissioner (the parent gates on isCommissioner). */
function SeasonRfaSection() {
  const currentSeason = useQuery(api.seasons.getCurrent);

  const rollover = useMutation(api.rollover.rolloverSeason);
  const setCutdownDate = useMutation(api.contractYears.setCutdownDate);
  const lockYearsNow = useMutation(api.contractYears.lockContractYearsNow);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [rolloverResult, setRolloverResult] = useState<{
    fromYear: number;
    toYear: number;
    contractsDecremented: number;
    contractsExpired: number;
  } | null>(null);
  const [cutdownInput, setCutdownInput] = useState<string>('');
  const [lockResult, setLockResult] = useState<number | null>(null);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setSubmitting(true);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onRollover = async () => {
    if (currentSeason === null || currentSeason === undefined) return;
    const y = currentSeason.year;
    const ok = window.confirm(
      `Roll over season ${y} → ${y + 1}?\n\nThis decrements all contract years; expiring contracts become RFAs.`,
    );
    if (!ok) return;
    await run(async () => {
      const result = await rollover({ expectedFromYear: y });
      setRolloverResult(result);
    });
  };

  // Renders an instant in league (Eastern) time.
  const formatEastern = (ms: number) =>
    `${new Date(ms).toLocaleString('en-US', { timeZone: 'America/New_York' })} ET`;

  const onSetCutdown = async (e: FormEvent) => {
    e.preventDefault();
    if (cutdownInput === '') {
      setError('Pick a cutdown date and time first.');
      return;
    }
    const cutdownAt = new Date(cutdownInput).getTime();
    if (Number.isNaN(cutdownAt)) {
      setError('Could not parse the cutdown date.');
      return;
    }
    if (
      currentSeason?.cutdownAt !== undefined &&
      currentSeason?.cutdownAt !== null &&
      currentSeason.cutdownAt !== cutdownAt
    ) {
      const ok = window.confirm(
        `Move the cutdown from ${formatEastern(currentSeason.cutdownAt)} to ${formatEastern(
          cutdownAt,
        )}?\n\nThe scheduled auto-lock is rescheduled to the new instant.`,
      );
      if (!ok) return;
    }
    await run(async () => {
      await setCutdownDate({ cutdownAt });
      setCutdownInput('');
    });
  };

  const onClearCutdown = async () => {
    const ok = window.confirm('Clear the cutdown date? The scheduled auto-lock is cancelled.');
    if (!ok) return;
    await run(() => setCutdownDate({}));
  };

  const onLockNow = async () => {
    const ok = window.confirm(
      'Lock contract years now? Every pending contract becomes final immediately.',
    );
    if (!ok) return;
    await run(async () => {
      const result = await lockYearsNow({});
      setLockResult(result.locked);
    });
  };

  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold">Season admin</h2>
      <p className="mt-1 text-xs text-gray-500">
        Auction controls live on the Auction page (visible to the commissioner only).
      </p>

      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Season rollover */}
      <div className="mt-3 rounded border bg-white p-4 shadow-sm">
        <h3 className="font-semibold">Season rollover</h3>
        {currentSeason === undefined ? (
          <p className="mt-2 text-sm text-gray-500">Loading…</p>
        ) : currentSeason === null ? (
          <p className="mt-2 text-sm text-gray-500">No current season — seed the league first.</p>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="text-sm">
              Current season: <strong>{currentSeason.year}</strong>
            </span>
            <button
              type="button"
              onClick={onRollover}
              disabled={submitting}
              className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
            >
              Roll over season
            </button>
          </div>
        )}
        {rolloverResult && (
          <div className="mt-3 rounded border border-green-300 bg-green-50 px-3 py-2 text-sm">
            Rolled over {rolloverResult.fromYear} → {rolloverResult.toYear}:{' '}
            <strong>{rolloverResult.contractsDecremented}</strong> contracts decremented,{' '}
            <strong>{rolloverResult.contractsExpired}</strong> expired (now RFA-eligible).
          </div>
        )}
      </div>

      {/* Phase 6f — cutdown date + contract-years lock */}
      {currentSeason != null && (
        <div className="mt-3 rounded border bg-white p-4 shadow-sm">
          <h3 className="font-semibold">Cutdown — contract-years lock</h3>
          <p className="mt-1 text-xs text-gray-500">
            Owners assign years to offseason acquisitions on /contracts until the cutdown instant
            auto-locks them. League deadline is Eastern Time.
          </p>

          {currentSeason.yearsLockedAt != null ? (
            <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
              <strong>Contract years are locked</strong> — since{' '}
              {formatEastern(currentSeason.yearsLockedAt)}.
              {lockResult !== null && (
                <span className="ml-1">
                  Locked <strong>{lockResult}</strong> pending contract
                  {lockResult === 1 ? '' : 's'}.
                </span>
              )}
            </div>
          ) : (
            <>
              <div className="mt-2 text-sm">
                Current cutdown:{' '}
                {currentSeason.cutdownAt != null ? (
                  <strong>{formatEastern(currentSeason.cutdownAt)}</strong>
                ) : (
                  <span className="text-gray-500">not set</span>
                )}
              </div>
              <form onSubmit={onSetCutdown} className="mt-2 flex flex-wrap items-end gap-3">
                <label className="flex flex-col text-sm">
                  <span className="text-gray-600 mb-1">Cutdown date &amp; time</span>
                  <input
                    type="datetime-local"
                    className="border rounded px-2 py-1"
                    value={cutdownInput}
                    onChange={(e) => setCutdownInput(e.target.value)}
                  />
                </label>
                {cutdownInput !== '' && !Number.isNaN(new Date(cutdownInput).getTime()) && (
                  <span className="pb-1 text-sm text-gray-600">
                    = <strong>{formatEastern(new Date(cutdownInput).getTime())}</strong>
                  </span>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50"
                >
                  {currentSeason.cutdownAt != null ? 'Move cutdown' : 'Set cutdown'}
                </button>
                {currentSeason.cutdownAt != null && (
                  <button
                    type="button"
                    onClick={onClearCutdown}
                    disabled={submitting}
                    className="rounded border px-3 py-2 text-sm disabled:opacity-50"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={onLockNow}
                  disabled={submitting}
                  className="rounded border border-red-300 px-3 py-2 text-sm text-red-700 disabled:opacity-50"
                >
                  Lock years now
                </button>
              </form>
              <p className="mt-1 text-xs text-gray-500">
                The picker uses your device's timezone; the league deadline is the ET instant echoed
                next to it.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
